class MultiCountryModal extends HTMLElement {
  constructor() {
    super();
    this.suggestionsScreen = this.querySelector('#MultiStoreSuggestions');
    this.optionsScreen = this.querySelector('#MultiStoreOptions');
    this.suggestionsTitle = this.querySelector('#MultiStoreSuggestionTitle');
    this.suggestionsSubtitle = this.querySelector('#MultiStoreSuggestionSubtitle');
    this.suggestionsContinue = this.querySelector('#MultiStoreSuggestionContinue');
    this.suggestionChange = this.querySelector('#MultiStoreSuggestionChange');
    this.optionsForm = this.querySelector('#multi-store-switcher-form');
    this.optionsContinue = this.querySelector('#MultiStoreOptionsContinue');
    this.optionsBack = this.querySelector('#MultiStoreOptionsBack');
    this.availableCountriesTemplate = this.querySelector('#MultiStoreCountryData');
    this.countrySelect = this.querySelector('#country-select');

    this.suggestionsElements = [this.suggestionsContinue, this.suggestionChange];
    this.optionsElements = [this.countrySelect, this.optionsBack, this.optionsContinue];

    this.onBodyClick = this.handleBodyClick.bind(this);
  }

  connectedCallback() {
    this.checkGeolocation();

    this.suggestionChange.addEventListener('click', this.showOptions.bind(this));
    this.suggestionsContinue.addEventListener('click', this.handleContinueClick.bind(this));
    this.optionsBack.addEventListener('click', this.showSuggestions.bind(this));

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    this.optionsForm.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  handleBodyClick(event) {
    const target = event.target;

    if (
      target !== this &&
      !target.closest('.switcher-modal-content') &&
      !target.closest('multi-country-modal-trigger')
    ) {
      this.hide();
    }
  }

  handleFormSubmit() {
    this.savePreferenceFromForm();
  }

  checkGeolocation() {
    if (sessionStorage.getItem('multi-store-dismissed')) {
      const data = JSON.parse(sessionStorage.getItem('multi-store-dismissed'));
      if (data.country === this.dataset.countryCode) {
        return;
      }
    }

    this.fetchUsersCountryData()
      .then((countryData) => {
        const geolocationCountryCode = countryData.detected_values.country.handle;
        const currentCountryCode = this.dataset.countryCode;

        if (geolocationCountryCode === currentCountryCode) {
          return;
        }

        const suggestedCountry = this.getSuggestedCountry(countryData);

        // If no suggested country is found, return.
        if (!suggestedCountry) {
          return;
        }

        this.updateTextContent(suggestedCountry);

        this.show(this.suggestionsContinue, 'suggestions');
      })
      .catch((error) => {
        console.error('Error fetching user country', error);
      });
  }

  getSuggestedCountry(countryData) {
    try {
      const availableCountriesData = JSON.parse(this.availableCountriesTemplate.innerHTML);

      const suggestedCountry = availableCountriesData.find((country) => {
        return country.iso_code === countryData?.detected_values?.country.handle;
      });

      return suggestedCountry || false;
    } catch {
      return false;
    }
  }

  updateTextContent(suggestedCountry) {
    /**
     * Countries that should be prefixed with `the` e.g. `the United Kingdom`
     */
    const theCountries = ['AE', 'GB', 'US'];

    const countryName = theCountries.includes(suggestedCountry.iso_code)
      ? `the ${suggestedCountry.name}`
      : suggestedCountry.name;

    const newSuggestionTitle = this.suggestionsTitle.innerText.replace('{{ country_name }}', countryName).trim();

    this.suggestionsTitle.innerText = newSuggestionTitle;

    const newSuggestionSubtitle = this.suggestionsSubtitle.innerText
      .replace('{{ country_iso_code }}', suggestedCountry.iso_code)
      .replace('{{ currency_symbol }}', suggestedCountry.currency.symbol)
      .replace('{{ currency_iso_code }}', suggestedCountry.currency.iso_code)
      .trim();

    this.suggestionsSubtitle.innerText = newSuggestionSubtitle;

    this.suggestionsContinue.innerText = this.suggestionsContinue.innerText
      .replace('{{ country_iso_code }}', suggestedCountry.iso_code.toLowerCase())
      .trim();

    this.suggestionsContinue.dataset.country = suggestedCountry.iso_code;
  }

  handleContinueClick(event) {
    const button = event.currentTarget;
    const countryCode = button.dataset.country;

    this.optionsElements.forEach((element) => {
      if (!element) {
        return;
      }
      element.removeAttribute('disabled');
    });

    this.countrySelect.value = countryCode;

    this.savePreferenceFromForm();

    this.optionsForm.submit();
  }

  savePreferenceFromForm() {
    const form = this.optionsForm;
    const data = new FormData(form);

    const selectedCounty = data.get('country_code');

    sessionStorage.setItem(
      'multi-store-dismissed',
      JSON.stringify({
        country: selectedCounty,
      })
    );
  }

  showSuggestions() {
    this.suggestionsScreen.style.display = 'block';
    this.optionsScreen.style.display = 'none';

    this.suggestionsElements.forEach((element) => {
      if (!element) {
        return;
      }
      element.removeAttribute('disabled');
    });

    this.optionsElements.forEach((element) => {
      if (!element) {
        return;
      }
      element.setAttribute('disabled', '');
    });

    const focusElement = this.suggestionsContinue;
    this.focusElement = focusElement;
    trapFocus(this, focusElement);
  }

  showOptions() {
    this.optionsScreen.style.display = 'block';
    this.suggestionsScreen.style.display = 'none';

    this.suggestionsElements.forEach((element) => {
      if (!element) {
        return;
      }
      element.setAttribute('disabled', '');
    });

    this.optionsElements.forEach((element) => {
      if (!element) {
        return;
      }
      element.removeAttribute('disabled');
    });

    const focusElement = this.countrySelect;
    this.focusElement = focusElement;
    trapFocus(this, focusElement);
  }

  disableSuggestions() {
    this.showOptions();
    this.optionsBack.style.display = 'none';
  }

  hide() {
    this.savePreferenceFromForm();
    this.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClick);
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.focusElement);
  }

  // Build the full country <option> list on first open, from the JSON in
  // #MultiStoreCountryData. Only the current country is server-rendered (to
  // keep initial DOM small), so this restores the full switcher on demand.
  populateCountryOptions() {
    if (this._countryOptionsPopulated || !this.countrySelect || !this.availableCountriesTemplate) return;

    let countries;
    try {
      countries = JSON.parse(this.availableCountriesTemplate.innerHTML);
    } catch {
      return;
    }

    this._countryOptionsPopulated = true;

    const current = this.dataset.countryCode;
    const fragment = document.createDocumentFragment();

    countries.forEach((country) => {
      const option = document.createElement('option');
      option.value = country.iso_code;
      option.textContent = country.name;
      if (country.iso_code === current) option.selected = true;
      fragment.appendChild(option);
    });

    this.countrySelect.replaceChildren(fragment);
  }

  show(focusElement, view = 'options') {
    this.populateCountryOptions();

    if (view === 'suggestions') {
      this.showSuggestions();
    } else {
      this.disableSuggestions();
    }

    this.focusElement = focusElement;
    this.setAttribute('open', '');
    document.body.addEventListener('click', this.onBodyClick);
    document.body.classList.add('overflow-hidden');
    trapFocus(this, focusElement);
  }

  fetchUsersCountryData() {
    return fetch('/browsing_context_suggestions.json').then((response) => response.json());
  }
}

customElements.define('multi-country-modal', MultiCountryModal);

class MultiCountryModalTrigger extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
  }

  connectedCallback() {
    this.button.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick() {
    const modal = document.querySelector('multi-country-modal');

    if (modal) modal.show();
  }
}

customElements.define('multi-country-modal-trigger', MultiCountryModalTrigger);
